import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useUpdateMyProfile } from "@/api/mutations";
import { useGetMyAccounts, useGetMyProfile } from "@/api/queries";
import FormError from "@/components/FormError";
import H2 from "@/components/H2";
import Input from "@/components/Input";
import RebrandPrimaryButton from "@/components/RebrandPrimaryButton";
// import SettingsMenu from '@/layouts/settingsMenu';
import Select from "@/components/Select";
import Spacer from "@/components/Spacer";
import countries from "@/data/countries";
import { getDefaultAccount, setDefaultAccount } from "@/store/appStateZustand";
// import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

function Settings() {
  const { t } = useTranslation(["other", "settings", "countries"]);
  const [errors] = useState({});
  const [obj, setObj] = useState({});
  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [renderComponent, setRenderComponent] = useState(false); // Added state for rendering

  const isTunecore = useUIStateStore(state => state.isTunecore);
  const profile = useGetMyProfile();
  const myAccounts = useGetMyAccounts();
  const createMutation = useUpdateMyProfile();

  // Default account state
  const [defaultAccountCode, setDefaultAccountCode] = useState(() => getDefaultAccount());

  // Get accounts for default selector
  const accounts = myAccounts?.data?.artistaccounts || [];
  const hasMultipleAccounts = accounts.length > 1;
  const defaultAccountDisplay = (() => {
    const acc = accounts.find(a => a.artistaccountcode === defaultAccountCode);
    return acc ? `${acc.accountname} (${acc.artistaccountcode})` : null;
  })();

  useEffect(() => {
    if (profile.isSuccess) {
      setObj({
        firstname: profile.data.firstname,
        lastname: profile.data.lastname,
        addressline1: profile.data.addressline1,
        addressline2: profile.data.addressline2,
        addressline3: profile.data.addressline3,
        towncity: profile.data.towncity,
        countystate: profile.data.countystate,
        postzipcode: profile.data.postzipcode,
        phonenumber: profile.data.phonenumber,
        countrycode: profile.data.countrycode,
      });
    }
  }, [profile.isSuccess, profile.data]);

  // setTimeout to allow for Select country list to be translated
  useEffect(() => {
    const timer = setTimeout(() => {
      setRenderComponent(true);
    }, 1000); // Adjust the delay as needed (in milliseconds)

    return () => clearTimeout(timer); // Clear the timeout when the component unmounts
  }, []); // Empty dependency array ensures the effect runs only once

  const fields = [
    {
      field: "firstname",
      label: t("common:first-name"),
      required: true,
    },
    {
      field: "lastname",
      label: t("common:last-name"),
      required: true,
    },
    {
      field: "addressline1",
      label: t("common:address-line-1"),
      required: true,
    },
    {
      field: "addressline2",
      label: t("common:address-line-2"),
      required: false,
    },
    {
      field: "addressline3",
      label: t("common:address-line-3"),
      required: false,
    },
    {
      field: "towncity",
      label: t("common:town-city"),
      required: true,
    },
    {
      field: "countystate",
      label: t("common:county"),
      required: true,
    },
    {
      field: "postzipcode",
      label: t("common:postcode"),
      required: true,
    },
    {
      field: "phonenumber",
      label: t("common:phone-number"),
      required: false,
    },
  ];

  const updateProfile = () => {
    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    // reset mutation
    createMutation.reset();
    // const valid = checkErrors();
    const valid = true;
    if (valid) {
      const data = obj;

      createMutation.mutate(data, {
        onSuccess: (response) => {
          if (response.failedvalidation) {
            setValidationErrorMessage(response.data.messages);
            setPending(false);
          }
          else {
            toast.success(t("settings:profile-updated-toast"));

            profile.refetch();

            setPending(false);
          }
        },
        onError: (error) => {
          setMutationErrorMessage(error.message);
          setPending(false);
        },
      });
    }
    else {
      setPending(false);
    }
  };

  if (!renderComponent) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div id="bars">
          <div className="bar" />
          <div className="bar" />
          <div className="bar" />
          <div className="bar" />
          <div className="bar" />
          <div className="bar" />
          <div className="bar" />
          <div className="bar" />
          <div className="bar" />
          <div className="bar" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap">
      <div className="w-full lg:min-h-screen-minus-header">
        <div className="mx-4 my-12 lg:mx-12 xl:mx-20 2xl:mx-40">
          <H2>{t("settings:update-details-subheader")}</H2>
          <Spacer />
          <div className="w-full pb-24">
            {/* Default Login Account - only show for users with multiple accounts */}
            {hasMultipleAccounts && (
              <>
                <Select
                  className={`select-search ${isTunecore ? "tunecore-select" : "font-sentric-alt text-base"} font-semibold`}
                  name="defaultAccount"
                  placeholder={defaultAccountDisplay
                    ? `${t("settings:default-login-account", "Default login account")}: ${defaultAccountDisplay}`
                    : t("settings:default-login-account", "Default login account")}
                  value={defaultAccountCode || ""}
                  onChange={(selected) => {
                    setDefaultAccount(selected || null);
                    setDefaultAccountCode(selected || null);
                    if (selected) {
                      const selectedAcc = accounts.find(acc => acc.artistaccountcode === selected);
                      toast.success(t("settings:default-account-updated", "Default account updated"), {
                        description: selectedAcc ? `${selectedAcc.accountname} (${selectedAcc.artistaccountcode})` : selected,
                        duration: 3000,
                      });
                    }
                    else {
                      toast.success(t("settings:default-account-cleared", "Default account cleared"), {
                        duration: 3000,
                      });
                    }
                  }}
                  options={[
                    { name: t("settings:no-default", "None (ask each login)"), value: "" },
                    ...accounts
                      .slice()
                      .sort((a, b) => a.accountname.localeCompare(b.accountname))
                      .map(acc => ({
                        name: `${acc.accountname} (${acc.artistaccountcode})`,
                        value: acc.artistaccountcode,
                      })),
                  ]}
                  search
                  filterOptions
                />
                <Spacer thin />
              </>
            )}

            <Input
              disabled
              type="text"
              placeholder={t("auth:email-placeholder")}
              value={profile?.data?.email || ""}
              onBlur={() => {}}
              onChange={() => { }}
              className="cursor-not-allowed settings-disabled-input"

            />
            <Spacer thin />

            {fields.map(f => (
              <div key={f.field}>
                <Input
                  required={f.required}
                  type="text"
                  error={errors[f.field]}
                  placeholder={f.label}
                  value={obj[f.field] || ""}
                  onBlur={() => {}}
                  onChange={(e) => {
                    setObj({ ...obj, [f.field]: e.target.value });
                  }}
                />
                <Spacer thin />
              </div>
            ))}
            <Select
              className={`select-search ${isTunecore ? "tunecore-select" : "font-sentric-alt text-base"} font-semibold required-star`}
              required
              name="country"
              placeholder={t("other:select-country-placeholder")}
              value={obj.countrycode}
              onChange={(selected) => {
                setObj({ ...obj, countrycode: selected });
              }}
              options={countries.map(option => ({
                name: t(`countries:${option.name}`),
                value: option.value,
              }))}
              search
              filterOptions
            />
            <Spacer />
            <FormError mutationError={mutationErrorMessage} validationError={validationErrorMessage} />
            <RebrandPrimaryButton
              onClick={updateProfile}
              disabled={pending}
              pending={pending}
            >
              {t("settings:update-button")}
            </RebrandPrimaryButton>
            <Spacer />
          </div>
        </div>
      </div>
    </div>
  );
}
export default Settings;
